/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState } from "react";
import "./App.scss";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
import SidePanel from "./components/side-panel/SidePanel";
import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import cn from "classnames";
import { LiveClientOptions } from "./types";
import Orb from "./components/background/Orb";
import DotGrid from "./components/background/DotGrid";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string" && process.env.NODE_ENV !== "production") {
  console.warn("REACT_APP_GEMINI_API_KEY not set in environment variables");
}

const apiOptions: LiveClientOptions = {
  apiKey: API_KEY,
};

function AppContent() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  
  // Get isSpeaking from LiveAPI context to control Orb's Force Hover State
  const { isSpeaking } = useLiveAPIContext();

  return (
    <div className="streaming-console">
      <SidePanel />
      <main>
        {/* Background layer with animated DotGrid behind Orb */}
        <div
          style={{ 
            width: '100%', 
            height: '100vh', 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            zIndex: 0,
            pointerEvents: 'none'
          }}
          aria-hidden="true"
        >
          {/* DotGrid fills the viewport behind the Orb */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            <DotGrid 
              dotSize={4}
              gap={10}
              baseColor="#080808"
              activeColor="#5227FF"
              proximity={120}
              shockRadius={250}
              shockStrength={5}
              resistance={750}
              returnDuration={1.5}
              isSpeaking={isSpeaking}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>

          {/* Centered Orb above DotGrid */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <Orb 
              hoverIntensity={0.5} 
              rotateOnHover={true} 
              hue={0} 
              forceHoverState={isSpeaking} 
            />
          </div>
        </div>
        <div className="main-app-area">
          {/* APP goes here */}
          <Altair />
          <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
            })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        </div>
        <div style={{ position: "relative", zIndex: 10 }}>
          <ControlTray
            videoRef={videoRef}
            supportsVideo={true}
            onVideoStreamChange={setVideoStream}
            enableEditingSettings={true}
          >
            {/* put your own buttons here */}
          </ControlTray>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <LiveAPIProvider options={apiOptions}>
        <AppContent />
      </LiveAPIProvider>
    </div>
  );
}

export default App;
